import PropTypes from 'prop-types';
import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import styled from 'styled-components';

import { config } from '_config';
import { theme } from '_constants/theme';
import { ROUTES, PARAMS } from '_constants/routes';
import { PROCESSING_CODES } from '_constants/processing';
import { FIELDS_KEYS } from '_constants/forms';
import { hexToRGBA } from '_helpers/theme';
import { createQueryString } from '_helpers/urls';
import { getProcessingLabel } from '_helpers/processing';
import { getEntityType } from '_helpers/filingFlow';

import { refreshActiveProductCode } from '_redux/products/slice';
import { refreshForm, refreshProducts } from '_redux/order/slice';
import { selectFormValues as selectFilingFormValues } from '_redux/order/selector';
import { selectGeolocation } from '_redux/general/selector';
import { selectPriceMapByProcessingCode } from '_redux/variants/selector';

import { transformRowsToIncludedProducts } from '../PackageSelectSection/helpers';

import Card from '_components/atomic/layouts/Card';
import StateAndEntitySelectForm from '_components/composed/forms/StateAndEntitySelectForm';

const StyledOuterContainer = styled.div`
  max-width: ${theme.spaces.large32X * 10}px;
  width: 100%;
`;

const StyledInnerTopContainer = styled.div`
  display: flex;
  justify-content: space-between;
  padding-top: ${theme.spaces.large4X}px;
  padding-left: ${((theme.spaces.large32X - theme.spaces.large25X) * 10) / 2}px;

  @media ${theme.devices.laptopS} {
    padding-left: ${theme.spaces.regular}px;
  }

  @media ${theme.devices.tablet} {
    flex-direction: column;
    padding-top: ${theme.spaces.smallX}px;
    padding-left: unset;
  }
`;

const StyledInnerBottomContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${theme.spaces.regular}px;

  span {
    display: inline-block;
    padding: ${theme.spaces.small2X}px;
    border-radius: 6px;
    background-color: ${hexToRGBA(theme.colors.indigo[600], 0.1)};
    color: ${theme.colors.indigo[600]};
  }

  @media ${theme.devices.tablet} {
    padding-bottom: ${theme.spaces.large12X}px;
  }
`;

const StyledFormContainer = styled.div`
  max-width: 520px;
  width: 100%;
`;

const StyledImageContainer = styled.div`
  picture {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    height: 100%;

    img {
      width: 92%;
    }
  }

  @media ${theme.devices.laptopS} {
    picture {
      display: flex;
      justify-content: flex-end;
      align-items: flex-end;
      height: 100%;
      width: 360px;

      img {
        width: 100%;
      }
    }
  }

  @media ${theme.devices.tablet} {
    padding-left: ${theme.spaces.largeX}px;
    position: relative;
    height: 386px;
    overflow: hidden;

    picture {
      position: absolute;
      width: unset;
      align-items: center;

      img {
        height: 386px;
        width: calc(386px * 1.4157);
      }
    }
  }
`;

const StyledDivider = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  gap: ${theme.spaces.regular * 2}px;
  padding: ${theme.spaces.regular}px;
`;

const StyledBullet = styled.div`
  height: 6px;
  width: 6px;
  border-radius: 50%;
  background-color: ${theme.colors.gray[200]};
  position: relative;
`;

const StyledLine = styled.div`
  width: 30%;
  height: 1px;
  background-color: ${theme.colors.gray[200]};

  @media ${theme.devices.tablet} {
    width: 40%;
  }
`;

const StateAndEntitySelectSection = ({ submitRef }) => {
  const router = useRouter();
  const dispatch = useDispatch();
  const filingFormValues = useSelector(selectFilingFormValues);
  const geolocation = useSelector(selectGeolocation);

  const priceMapByProcessingCode = useSelector(
    selectPriceMapByProcessingCode(PROCESSING_CODES.gold)
  );

  const handleSubmit = useCallback(
    (values) => {
      if (values) {
        dispatch(refreshActiveProductCode(values[FIELDS_KEYS.entity]));
        dispatch(refreshForm({ ...values }));
        dispatch(
          refreshProducts([
            values[FIELDS_KEYS.entity],
            ...transformRowsToIncludedProducts({
              activeProcessingCode: values[FIELDS_KEYS.processingOption],
            }),
          ])
        );

        const queryParams = {
          [PARAMS.entityType]: getEntityType(values[FIELDS_KEYS.entity]),
          [PARAMS.entityState]: values[FIELDS_KEYS.organizedState],
          [PARAMS.packageId]: getProcessingLabel(
            values[FIELDS_KEYS.processingOption]
          ),
        };

        router.push(
          `${ROUTES.incPackage}${createQueryString(queryParams)}`,
          undefined,
          { shallow: true }
        );
      }
    },
    [dispatch, router]
  );

  return (
    <StyledOuterContainer>
      <StyledInnerTopContainer>
        <StyledFormContainer>
          <StateAndEntitySelectForm
            submitRef={submitRef}
            prices={priceMapByProcessingCode}
            geolocation={geolocation}
            defaultValues={filingFormValues}
            onSubmit={handleSubmit}
          />
        </StyledFormContainer>
        <StyledImageContainer>
          <picture>
            <source
              srcSet={`${config.basePath}/images/v4-top-bizfiz-llc.png`}
              type="image/png"
            />
            <img
              src={`${config.basePath}/images/v4-top-bizfiz-llc.png`}
              alt="Biz Fiz LLC"
            />
          </picture>
        </StyledImageContainer>
      </StyledInnerTopContainer>
      <StyledInnerBottomContainer>
        <StyledDivider>
          <StyledLine />
          <StyledBullet />
          <StyledLine />
        </StyledDivider>
        <Card.Text size={theme.fontSizes.largeX} weight={700} centered desktop>
          Bootstrapped, Founder Led, Independently Owned <span>Since 2004</span>
          &nbsp;with <span>Over 1,000,000 Entrepreneurs</span> Served!
        </Card.Text>
        <Card.Text size={theme.fontSizes.regular} weight={700} centered mobile>
          Bootstrapped, Founder Led, Independently Owned <span>Since 2004</span>
          &nbsp;with <span>Over 1,000,000 Entrepreneurs</span> Served!
        </Card.Text>
        <StyledDivider>
          <StyledLine />
          <StyledBullet />
          <StyledLine />
        </StyledDivider>
      </StyledInnerBottomContainer>
    </StyledOuterContainer>
  );
};

StateAndEntitySelectSection.propTypes = {
  submitRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.any }),
  ]),
};

export default StateAndEntitySelectSection;
